/**
 * Block treatments: mark blocks whose grammar earns one, style them in CSS.
 *
 * This pass runs AFTER the terminal inline transform, and that ordering is the
 * design rather than an accident. By then a warning-led paragraph already
 * begins with a provenance-backed glyph, so "is this warning-led" is answered
 * by reading a trusted mark instead of re-deriving the leading token — which
 * would be a second rendered-order traversal, the one thing the inline-run
 * cursor exists to prevent. It also means the marker is consumed exactly once:
 * this pass never removes text, it re-labels a glyph the inline pass already
 * minted and annotates the block that owns it.
 *
 * Marking is most of what this does, but not all of it: rule normalization and
 * display-empty suppression REMOVE nodes, because a suppressed rule has to be
 * gone from the tree rather than merely invisible — an `hr` hidden by CSS is
 * still the last child, and would still capture the streaming cursor.
 */
import {
    getEditorialProvenance,
    isEditorialGenerated,
    markEditorial,
} from "./editorial-provenance";
import {
    classifyEditorialCapabilities,
    isEditorialTouchable,
    type EditorialHastNode,
} from "./editorial-capabilities";
import {
    POLLY_EDITORIAL_V1,
    type EditorialBlockRole,
    type EditorialGlyphUsage,
} from "./editorial-policy";
import { resolveContainerOutcome } from "./editorial-container-outcomes";
import { firstMeaningfulChild, isIgnorableNode } from "./editorial-inline-run";

interface HastNodeLike {
    type?: string;
    tagName?: string;
    value?: string;
    properties?: Record<string, unknown>;
    children?: HastNodeLike[];
}

/** hast's normalized spelling of `data-editorial-block`. */
export const BLOCK_ROLE_PROPERTY = "dataEditorialBlock";

const WARNING_CALLOUT: EditorialBlockRole = "warning-callout";
const CHECKLIST_ITEM: EditorialBlockRole = "checklist-item";

/** hast's normalized spelling of `data-editorial-excerpt`. */
export const EXCERPT_PROPERTY = "dataEditorialExcerpt";

/**
 * Which semantics may lead which block, read from the manifest rather than
 * listed here.
 *
 * The manifest already declares `blockRoles` per vocabulary entry, so a second
 * list in this module would be a copy that drifts: adding a semantic that may
 * head a checklist would silently not work until someone remembered to edit
 * both. Built once at module scope — the manifest is deep-frozen.
 */
const semanticsForRole = (role: EditorialBlockRole): ReadonlySet<string> =>
    new Set(
        POLLY_EDITORIAL_V1.vocabulary
            .filter(
                (entry): entry is typeof entry & { semantic: string } =>
                    entry.disposition === "SEMANTIC" &&
                    (entry.blockRoles ?? []).includes(role)
            )
            .map((entry) => entry.semantic)
    );

const WARNING_SEMANTICS = semanticsForRole(WARNING_CALLOUT);
const CHECKLIST_SEMANTICS = semanticsForRole(CHECKLIST_ITEM);

/**
 * The leading glyph of a block, descending exactly as the grammar says.
 *
 * Two descents, both required by real markdown rather than by taste:
 *
 * - through block-scan TRANSPARENT wrappers, so `- **✅ Covered**` is led by the
 *   check. The inline pass mints the glyph wherever the marker was, which for
 *   bold text is inside the `strong`, so a direct-children-only check would see
 *   an element that is not a glyph and decline every emphasized item.
 * - through a leading `p`, because a LOOSE list item wraps its content in one
 *   and a tight item does not. Asking at whichever node actually holds the
 *   inline content is what makes both classify identically.
 *
 * Anything else that occupies the run stops the descent and returns nothing: a
 * word, an image, an opaque subtree, or a task-list checkbox all mean the block
 * is not marker-led, even when a marker follows them.
 *
 * Provenance decides, never the `data-editorial-glyph` attribute beside it —
 * that attribute is authorable, so reading it would let a message promote its
 * own block. The structural pass scrubs forged copies upstream; this is the
 * second, independent defence.
 */
const leadingGlyph = (block: HastNodeLike): HastNodeLike | undefined => {
    const seen = new Set<unknown>();
    let current = block;
    for (;;) {
        if (seen.has(current)) return undefined;
        seen.add(current);

        const first = firstMeaningfulChild(current);
        if (!first || first.type !== "element") return undefined;
        if (getEditorialProvenance(first)?.kind === "glyph") return first;

        const { blockScan } = classifyEditorialCapabilities(
            first as EditorialHastNode
        );
        if (blockScan !== "transparent") return undefined;
        current = first;
    }
};

/** The leading glyph, only when it carries the semantic asked for. */
const leadingGlyphOfSemantic = (
    block: HastNodeLike,
    semantics: ReadonlySet<string>
): HastNodeLike | undefined => {
    const glyph = leadingGlyph(block);
    if (!glyph) return undefined;
    const semantic = getEditorialProvenance(glyph)?.semantic;
    return semantic !== undefined && semantics.has(semantic)
        ? glyph
        : undefined;
};

/**
 * Whether a block role on this node is OURS.
 *
 * The attribute alone proves nothing. It is the reserved namespace, and the
 * structural pass scrubs forged spellings upstream — but a check that reads the
 * attribute directly is trusting that scrub to have run, and this layer's whole
 * trust model is that PROVENANCE decides while the attribute is only how a test
 * observes the decision.
 *
 * It matters more here than it looks: the block treatments are styled by CSS
 * selecting on this attribute, so a forgery that survives to render IS the
 * treatment. Authored markup would become a warning callout on the strength of
 * a string the model wrote.
 */
const ownBlockRole = (node: HastNodeLike, property: string): boolean =>
    node.properties?.[property] !== undefined &&
    getEditorialProvenance(node)?.kind === "structural-mark";

/**
 * Drop a block role this layer did not write, so the node is judged on its
 * grammar like any other. Deleted in place rather than rebuilt: the markdown
 * memo comparator tests `properties` by reference.
 */
const stripUnprovenRole = (node: HastNodeLike, property: string): void => {
    if (node.properties?.[property] !== undefined) {
        delete node.properties[property];
    }
};

/**
 * Elevate one block, if its grammar earns it. Returns whether it did, so the
 * caller does not have to re-ask.
 *
 * Which blocks are eligible is the caller's question; what elevation MEANS is
 * this function's, and it is the same for every role.
 */
const elevateBlock = (
    node: HastNodeLike,
    semantics: ReadonlySet<string>,
    role: EditorialBlockRole,
    usage: EditorialGlyphUsage
): boolean => {
    // Idempotent on OUR marks: a second run over an already-marked tree
    // changes nothing. A role this layer did not write is a forgery, dropped
    // here so the block is then judged on its grammar like any other.
    if (ownBlockRole(node, BLOCK_ROLE_PROPERTY)) return false;
    stripUnprovenRole(node, BLOCK_ROLE_PROPERTY);

    const glyph = leadingGlyphOfSemantic(node, semantics);
    if (!glyph) return false;

    // Re-label the marker for the context it now sits in. The manifest owns the
    // wording; this only names the usage, and `EditorialGlyph` still refuses
    // the pair if the manifest does not declare it.
    //
    // The semantic is read back rather than restated: the semantics sets are
    // derived from the manifest, so writing a literal would mislabel the glyph
    // the moment a second semantic declares this role.
    const semantic = getEditorialProvenance(glyph)?.semantic;
    if (semantic === undefined) return false;

    markEditorial(glyph, { kind: "glyph", semantic, usage });

    node.properties = {
        ...(node.properties ?? {}),
        [BLOCK_ROLE_PROPERTY]: role,
    };
    // `structural-mark`, not a minted kind: the block is the author's, and this
    // records a fact about it. Marking it generated would tell every other
    // consumer the layer owns its content, which it does not.
    markEditorial(node, { kind: "structural-mark" });
    return true;
};

const elevateWarningCallout = (paragraph: HastNodeLike): boolean =>
    elevateBlock(
        paragraph,
        WARNING_SEMANTICS,
        WARNING_CALLOUT,
        "warning-callout-marker"
    );

/**
 * Ordered lists never reach here — see the walker. GFM task-list items exclude
 * themselves without a special case: their `<input type="checkbox">` is the
 * first thing occupying the run, so the descent stops on it and finds no
 * leading glyph. Precedence 2 falls out of the grammar rather than being
 * restated, which is the difference between a rule and a coincidence.
 */
const elevateChecklistItem = (item: HastNodeLike): boolean =>
    elevateBlock(item, CHECKLIST_SEMANTICS, CHECKLIST_ITEM, "checklist-marker");

/**
 * Mark a blockquote as a document excerpt.
 *
 * Container only. A blockquote is a verbatim zone: the words are the
 * document's, and the inline pass already declines to touch them, so this adds
 * no marker and consumes nothing. All it records is that the container is an
 * excerpt, which is the whole treatment — the styling difference is what tells
 * a reader these words were lifted rather than written.
 */
const markDocumentExcerpt = (quote: HastNodeLike): boolean => {
    if (ownBlockRole(quote, EXCERPT_PROPERTY)) return false;
    stripUnprovenRole(quote, EXCERPT_PROPERTY);
    quote.properties = {
        ...(quote.properties ?? {}),
        [EXCERPT_PROPERTY]: "true",
    };
    markEditorial(quote, { kind: "structural-mark" });
    return true;
};

/** Rendered text of a subtree, ignoring nodes that draw nothing. */
const renderedText = (node: HastNodeLike): string => {
    const parts: string[] = [];
    const stack = [node];
    const seen = new Set<unknown>();
    while (stack.length > 0) {
        const current = stack.pop() as HastNodeLike;
        if (seen.has(current)) continue;
        seen.add(current);
        if (current.type === "text") {
            parts.push(current.value ?? "");
            continue;
        }
        const children = current.children ?? [];
        for (let index = children.length - 1; index >= 0; index--) {
            stack.push(children[index]);
        }
    }
    return parts.join("");
};

/**
 * Something other than display text that the reader still sees — a minted
 * glyph, an image, a nested list.
 */
const hasSubstantiveChild = (node: HastNodeLike): boolean =>
    (node.children ?? []).some(
        (child) =>
            child.type === "element" &&
            (getEditorialProvenance(child) !== undefined ||
                child.tagName === "img" ||
                child.tagName === "ul" ||
                child.tagName === "ol" ||
                child.tagName === "table")
    );

/**
 * Drop containers the transform emptied.
 *
 * This is where the container-outcome machinery finally does work: up to now
 * no treatment removed text, so nothing could be emptied. A paragraph
 * holding only a decorative emoji renders as `<p></p>` without this — an
 * invisible box that still takes vertical space, still counts as a sibling for
 * rule adjacency, and can still capture the streaming cursor as the last child.
 *
 * The decision is delegated rather than re-derived: `resolveContainerOutcome`
 * already knows that an emptied `li` in an ORDERED list must be retained (its
 * number is positional) and that a cell must be retained (its column is), and
 * restating any of that here is how the two would drift.
 */
const suppressEmptied = (parent: HastNodeLike): void => {
    const postorder: HastNodeLike[] = [];
    const stack: Array<
        | { readonly kind: "enter"; readonly node: HastNodeLike }
        | { readonly kind: "exit"; readonly node: HastNodeLike }
    > = [{ kind: "enter", node: parent }];
    const seen = new Set<unknown>();

    while (stack.length > 0) {
        const frame = stack.pop() as (typeof stack)[number];
        if (frame.kind === "exit") {
            postorder.push(frame.node);
            continue;
        }
        if (seen.has(frame.node)) continue;
        seen.add(frame.node);
        stack.push({ kind: "exit", node: frame.node });
        const children = frame.node.children ?? [];
        for (let index = children.length - 1; index >= 0; index--) {
            stack.push({ kind: "enter", node: children[index] });
        }
    }

    const displayNonBlank = new Map<HastNodeLike, boolean>();
    for (const node of postorder) {
        const children = node.children;
        let suppressedHere = false;

        if (children) {
            for (let index = children.length - 1; index >= 0; index--) {
                const child = children[index];
                if (child.type !== "element") continue;
                const { contract } = classifyEditorialCapabilities(
                    child as EditorialHastNode
                );
                if (!isEditorialTouchable(contract)) continue;

                const outcome = resolveContainerOutcome(
                    child as EditorialHastNode,
                    {
                        displayEmpty: !displayNonBlank.get(child),
                        hasSubstantiveChild: hasSubstantiveChild(child),
                        inOrderedList: node.tagName === "ol",
                    }
                );
                const emptiedByTransform =
                    getEditorialProvenance(child)?.kind === "emptied-container";
                if (outcome === "suppress" && emptiedByTransform) {
                    children.splice(index, 1);
                    suppressedHere = true;
                }
            }
        }

        const nonBlank = (node.children ?? []).some((child) =>
            child.type === "text"
                ? (child.value ?? "").trim() !== ""
                : displayNonBlank.get(child) === true
        );
        displayNonBlank.set(node, nonBlank);

        if (suppressedHere && !nonBlank && !hasSubstantiveChild(node)) {
            markEditorial(node, { kind: "emptied-container" });
        }
    }
};

/**
 * A sibling that occupies the reader's attention, for adjacency purposes.
 *
 * "Renders nothing" is asked of the shared predicate rather than re-stated
 * here: a second copy would answer differently for node types neither of us
 * enumerated, and adjacency is decided against the same tree the inline cursor
 * walks.
 */
const isSubstantive = (node: HastNodeLike): boolean =>
    node.type === "element" || (node.type === "text" && !isIgnorableNode(node));

const isHeading = (tag: string | undefined): boolean =>
    /^h[1-6]$/.test(tag ?? "");

/**
 * Rule normalization, decided against the PLANNED DISPLAY TREE.
 *
 * Order matters and is the whole subtlety: suppression runs first, so a rule
 * whose only remaining neighbour was a decorative-only paragraph is correctly
 * seen as trailing. Deciding against the authored tree instead would leave a
 * rule dangling at the end of an answer.
 *
 * Heading adjacency is the exception and comes from PROVENANCE, not from the
 * tree — `rehypeInferTableTitle` may already have removed the heading to use it
 * as a table title, so by now the evidence only exists as the mark the
 * structural pass recorded. Reading the tree here would silently keep a rule
 * that the reader sees directly under a heading.
 */
const normalizeRules = (root: HastNodeLike): void => {
    const children = root.children;
    if (!children) return;

    const previousSubstantive: Array<HastNodeLike | undefined> = [];
    let previous: HastNodeLike | undefined;
    for (let index = 0; index < children.length; index++) {
        previousSubstantive[index] = previous;
        if (isSubstantive(children[index])) previous = children[index];
    }

    let hasFollowingSubstantive = false;

    for (let index = children.length - 1; index >= 0; index--) {
        const node = children[index];
        if (node.type !== "element" || node.tagName !== "hr") {
            if (isSubstantive(node)) hasFollowingSubstantive = true;
            continue;
        }

        const provenance = getEditorialProvenance(node);
        const headingAdjacent =
            isHeading(provenance?.authoredPrevious) ||
            isHeading(provenance?.authoredNext);

        const previous = previousSubstantive[index];
        const followsRule =
            previous?.type === "element" && previous.tagName === "hr";

        if (
            headingAdjacent ||
            previous === undefined ||
            !hasFollowingSubstantive ||
            followsRule
        ) {
            children.splice(index, 1);
        } else {
            hasFollowingSubstantive = true;
        }
    }
};

/** hast's normalized spelling of `data-editorial-lead-in`. */
export const LEAD_IN_PROPERTY = "dataEditorialLeadIn";

/**
 * Term/description lead-in: `**Term**: description`, precedence 5.
 *
 * Both spellings are accepted — the colon inside the bold or immediately after
 * it — because both are authored constantly and mean the same thing to a
 * reader. Nothing else qualifies: a semicolon does not, and neither does bare
 * bold, which is used for ordinary emphasis all through Polly's prose and
 * would restyle half an answer if it counted.
 *
 * A leading glyph is skipped so this can coexist with the checklist treatment
 * on one item (`- ✅ **Coverage**: Included`).
 *
 * The mark goes on the `strong` rather than the item: it is the TERM that is
 * typeset, and putting it on the item would make the rule that styles it
 * reach the description too.
 */
const markLeadIn = (block: HastNodeLike): boolean => {
    const root =
        firstMeaningfulChild(block)?.tagName === "p"
            ? (firstMeaningfulChild(block) as HastNodeLike)
            : block;

    const meaningful = (root.children ?? []).filter(
        (child) =>
            !(child.type === "text" && (child.value ?? "").trim() === "") &&
            child.type !== "comment"
    );

    // Skip a leading glyph, so checklist and lead-in can share an item.
    const start =
        meaningful[0]?.type === "element" &&
        getEditorialProvenance(meaningful[0])?.kind === "glyph"
            ? 1
            : 0;

    const term = meaningful[start];
    if (!term || term.type !== "element" || term.tagName !== "strong") {
        return false;
    }
    if (term.properties?.[LEAD_IN_PROPERTY] !== undefined) return false;

    const colonInside = renderedText(term).trimEnd().endsWith(":");
    const next = meaningful[start + 1];
    const colonAfter =
        next?.type === "text" && (next.value ?? "").trimStart().startsWith(":");
    if (!colonInside && !colonAfter) return false;

    term.properties = {
        ...(term.properties ?? {}),
        [LEAD_IN_PROPERTY]: "true",
    };
    markEditorial(term, { kind: "structural-mark" });
    return true;
};

/**
 * Mark a quote and every quote nested inside it.
 *
 * MARKING IS NOT DESCENT, and that distinction is the whole adjudication. The
 * general walk refuses to enter a blockquote because it classifies `verbatim`,
 * which is what keeps checklist elevation and every future treatment out of
 * quoted words. That refusal is untouched.
 *
 * This walk carries no treatment. It finds blockquotes and annotates the
 * container, which the call site already argues is not a content edit: the
 * verbatim guarantee is about the words, not about the box they sit in. That
 * argument does not weaken at depth two — an inner quote is still a box.
 *
 * The alternative was to leave the inner quote unmarked and lean on CSS
 * inheritance, which works today only because the treatment is `font-style:
 * normal`. A border, padding or background would make the inner quote diverge
 * silently, and a contract that holds only while a stylesheet stays one property
 * long is not a contract.
 *
 * Generated subtrees are skipped: our own minted markup is not a source quote.
 */
const markExcerptChain = (quote: HastNodeLike): void => {
    const seen = new Set<unknown>();
    const stack: HastNodeLike[] = [quote];

    while (stack.length > 0) {
        const node = stack.pop() as HastNodeLike;
        if (seen.has(node)) continue;
        seen.add(node);

        if (node.type === "element" && node.tagName === "blockquote") {
            markDocumentExcerpt(node);
        }

        for (const child of node.children ?? []) {
            if (child.type !== "element") continue;
            if (isEditorialGenerated(child as EditorialHastNode)) continue;
            stack.push(child);
        }
    }
};

/**
 * Checklist items and excerpts, at any depth.
 *
 * Lists nest legitimately — a list inside a list item — so unlike the warning
 * callout this is not a top-level rule and the walk is the point rather than a
 * hazard. Verified: a `ul` nested in a `ul`, and a `ul` nested in an `ol`, both
 * elevate.
 *
 * Quotes are the exception. Only the OUTERMOST blockquote is marked: a quote
 * classifies `verbatim`, so descent stops at it and a quote
 * nested inside a quote never gets its own mark. Measured — two blockquotes,
 * one mark. That is invisible today because the excerpt treatment is
 * `font-style: normal`, which inherits, but any non-inheriting property added
 * to the treatment later would make the inner quote diverge from the outer.
 *
 * `ol` is skipped entirely, and that is where the ordered-list exclusion lives:
 * an ordered list's numbers ARE its markers, and a glyph competing with them
 * reads as two markers for one item. Its children still get visited, so a `ul`
 * nested inside an `ol` still works.
 *
 * Verbatim, protocol, source-model and generated subtrees are refused whole:
 * a list inside a code block or a table is not this layer's to annotate, and
 * the classifier is asked rather than a tag list so there is no second
 * predicate to drift.
 */
const visitNested = (node: HastNodeLike): void => {
    const stack = [...(node.children ?? [])].reverse();
    const seen = new Set<unknown>([node]);
    while (stack.length > 0) {
        const child = stack.pop() as HastNodeLike;
        if (seen.has(child)) continue;
        seen.add(child);
        if (child.type !== "element") continue;

        if (child.tagName === "blockquote") markExcerptChain(child);

        const { contract } = classifyEditorialCapabilities(
            child as EditorialHastNode
        );
        if (!isEditorialTouchable(contract)) continue;

        if (child.tagName === "ul") {
            for (const item of child.children ?? []) {
                if (item.type === "element" && item.tagName === "li") {
                    elevateChecklistItem(item);
                    markLeadIn(item);
                }
            }
        }

        const children = child.children ?? [];
        for (let index = children.length - 1; index >= 0; index--) {
            stack.push(children[index]);
        }
    }
};

/**
 * Top-level only (precedence 4).
 *
 * A paragraph nested in a list item or a blockquote is deliberately excluded:
 * inside an `li` the warning is one item among several and a callout would
 * nest a box inside a bullet, and inside a blockquote the words are the
 * document's rather than Polly's, so promoting them would editorialize
 * evidence. Both render the marker inline instead, which the inline pass has
 * already done by the time this runs.
 *
 * Expressed as "direct element child of the root" because that is exactly what
 * top-level means once remark has built the tree — no ancestor walk, nothing to
 * keep in sync with the set of containers that may nest a paragraph.
 */
export const rehypeApplyEditorialBlocks =
    () =>
    (tree: unknown): void => {
        const root = tree as HastNodeLike;

        for (const child of root.children ?? []) {
            if (child.type !== "element" || child.tagName !== "p") continue;

            // Asked of the single classifier rather than a local tag test, so
            // this stays correct if `p` ever gains a contract.
            //
            // Honest about what it does today: a `p` classifies `none`
            // whatever its attributes, so this currently skips nothing —
            // measured, not assumed. It is not the protection it reads like,
            // and the real reason a verbatim or source-model paragraph is safe
            // is that no such paragraph exists. Kept because the alternative is
            // a second place that assumes paragraphs are always plain, which is
            // the divergence the classifier exists to prevent.
            const { contract } = classifyEditorialCapabilities(
                child as EditorialHastNode
            );
            if (!isEditorialTouchable(contract)) continue;

            elevateWarningCallout(child);
        }

        // Suppress first, then normalize rules against what survives, then
        // the nested treatments. Rules are decided before checklist/excerpt
        // marking because neither can change whether a sibling is substantive.
        suppressEmptied(root);
        normalizeRules(root);

        // Lists and blockquotes nest, so these two walk. The warning callout
        // above deliberately does not — its rule IS "top level", and walking
        // for it would be the bug the loose-list test now pins.
        visitNested(root);
    };
